import * as React from 'react';

import {ActionMenu} from 'shells/action-menu/action-menu';
import {ConfirmationDialog} from 'shells/confirmation-dialog/confirmation-dialog';
import {CogRegularIcon} from 'shells/icon/svg/cog-regular-icon';
import {EyeRegularIcon} from 'shells/icon/svg/eye-regular-icon';
import {SyncRegularIcon} from 'shells/icon/svg/sync-regular-icon';
import {TrashRegularIcon} from 'shells/icon/svg/trash-regular-icon';

import type {GetContextDocuments_contextDocuments as ContextDocument} from 'nutshell-graphql-types';

import {
    useToggleContextDocumentUsage,
    useRemoveContextDocument,
    useRecrawlContextDocuments,
} from '../../graphql/hooks';

type Props = {
    document: ContextDocument;
    onRefetch: () => Promise<any>;
    onSelectDocument: (document: ContextDocument) => void;
};

export function ContextDocumentRowActionMenu(props: Props) {
    const [isConfirmingDelete, setIsConfirmingDelete] = React.useState<boolean>(false);

    const {toggleContextDocumentUsage} = useToggleContextDocumentUsage();
    const {removeContextDocument} = useRemoveContextDocument();
    const {recrawlContextDocuments} = useRecrawlContextDocuments();

    const isWebpage = props.document.sourceType === 'WEB_PAGE';

    const options = [
        {
            label: 'View content',
            icon: <EyeRegularIcon size={18} wrapWithDiv={false} />,
            handleSelect: () => props.onSelectDocument(props.document),
        },
        {
            label: props.document.isIncluded ? 'Disable' : 'Enable',
            icon: <CogRegularIcon size={18} wrapWithDiv={false} />,
            handleSelect: () =>
                toggleContextDocumentUsage({
                    documentId: props.document.id,
                    isEnabled: !props.document.isIncluded,
                    sourceType: props.document.sourceType,
                }),
        },
    ];

    if (isWebpage) {
        options.push({
            label: 'Recrawl',
            icon: <SyncRegularIcon size={18} wrapWithDiv={false} />,
            handleSelect: () => {
                recrawlContextDocuments({
                    documentId: props.document.id,
                });
                props.onRefetch();
            },
        });
    }

    options.push({
        label: 'Delete',
        icon: <TrashRegularIcon size={18} wrapWithDiv={false} />,
        handleSelect: () => setIsConfirmingDelete(true),
        // @ts-expect-error
        variant: 'destructive',
    });

    const handleDelete = () => {
        removeContextDocument({
            documentId: props.document.id,
            sourceType: props.document.sourceType,
        });
        setIsConfirmingDelete(false);
        props.onRefetch();
    };

    return (
        <>
            <ActionMenu options={options} size='small' hasHoverBackground={true} />
            {isConfirmingDelete && (
                <ConfirmationDialog
                    title={`Are you sure you want to delete this ${
                        isWebpage ? 'webpage' : 'document'
                    }?`}
                    message='This will be permanently removed from your knowledge base. This action cannot be undone.'
                    onCancel={(e) => {
                        // @ts-expect-error
                        e.stopPropagation();
                        setIsConfirmingDelete(false);
                    }}
                    onConfirm={(e) => {
                        e.stopPropagation();
                        handleDelete();
                    }}
                    confirmText='Delete'
                    // @ts-expect-error
                    isDestructive={true}
                />
            )}
        </>
    );
}
